import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32">
      {/* Animated Background Layers */}
      <div className="absolute inset-0 z-0">
        {/* Base Horizontal Gradient - Darker blues, 75% darkest */}
        <div className="absolute inset-0 bg-gradient-to-r from-deepspace-500 from-0% via-deepspace-500 via-75% to-deepspace-400 to-100%"></div>
        
        {/* Dynamic Gradient Orbs - Darker blue only */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute w-[600px] h-[600px] bg-cyber-blue/10 rounded-full blur-[100px] animate-pulse-slow"
            style={{
              left: `${20 + (mousePosition.x / window.innerWidth) * 15}%`,
              top: `${15 + (mousePosition.y / window.innerHeight) * 10}%`,
            }}
          ></div>
          <div 
            className="absolute w-[500px] h-[500px] bg-cyber-blue/8 rounded-full blur-[100px] animate-pulse-slow"
            style={{
              right: `${10 + (mousePosition.x / window.innerWidth) * 15}%`,
              top: `${25 + (mousePosition.y / window.innerHeight) * 15}%`,
              animationDelay: '1.5s'
            }}
          ></div>
          <div 
            className="absolute w-[400px] h-[400px] bg-cyber-blue/6 rounded-full blur-[100px] animate-pulse-slow"
            style={{
              left: `${50 + (mousePosition.x / window.innerWidth) * 10}%`,
              bottom: `${15 + (mousePosition.y / window.innerHeight) * 20}%`,
              animationDelay: '3s'
            }}
          ></div>
        </div>

        {/* Circuit Pattern Overlay */}
        <div className="absolute inset-0 opacity-5">
          <img 
            src="/images/all/circuit-board-pattern-background.png" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>

        {/* Particle Network Overlay */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/images/all/particle-network.png" 
            alt="" 
            className="w-full h-full object-cover animate-pulse-slow"
          />
        </div>

        {/* Floating Particles - Darker blue only */}
        <div className="absolute inset-0 hidden lg:block">
          <div className="absolute top-20 right-20 w-2 h-2 bg-cyber-blue/60 rounded-full animate-float"></div>
          <div className="absolute top-40 right-1/4 w-3 h-3 bg-cyber-blue/50 rounded-full animate-float-delayed"></div>
          <div className="absolute bottom-32 left-20 w-2 h-2 bg-cyber-blue/40 rounded-full animate-float"></div>
          <div className="absolute bottom-20 left-1/3 w-4 h-4 bg-cyber-blue/40 rounded-full animate-float-delayed opacity-60"></div>
          <div className="absolute top-1/3 left-10 w-2 h-2 bg-cyber-blue/50 rounded-full animate-float opacity-60"></div>
          <div className="absolute bottom-1/3 right-10 w-3 h-3 bg-cyber-blue/50 rounded-full animate-float-delayed opacity-60"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          
          {/* Left Column - Text Content */}
          <div className="space-y-8 text-center lg:text-left">
            
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                <span className="text-text-primary">
                  Backup & Secure
                </span>
                <br />
                <span className="inline-block bg-gradient-to-r from-cyber-blue via-cyber-gold to-cyber-green bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
                  Your Crypto Assets
                </span>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-text-secondary leading-relaxed max-w-2xl mx-auto lg:mx-0">
                AI-driven cold wallet that safeguards your digital assets. Connect DApps to mobile wallets, recover lost funds, and manage your crypto with military-grade protection.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-row gap-3 justify-center lg:justify-start relative z-30">
              <Link 
                to="/auth"
                className="group relative px-5 py-2.5 bg-gradient-to-r from-cyber-blue to-cyber-gold text-white font-semibold text-sm rounded-lg overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyber-blue/50"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Connect Wallet
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyber-gold to-cyber-blue opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              
              <Link 
                to="/auth"
                className="group px-5 py-2.5 border-2 border-cyber-blue/50 text-cyber-blue font-semibold text-sm rounded-lg backdrop-blur-sm transition-all duration-300 hover:bg-cyber-blue/10 hover:border-cyber-blue hover:scale-105"
              >
                <span className="flex items-center justify-center gap-2">
                  Create Wallet
                </span>
              </Link>
            </div>

          </div>

          {/* Right Column - 3D Visualization */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-full max-w-6xl">
              
              {/* Mobile Background Enhancement */}
              <div className="absolute inset-0 lg:hidden bg-gradient-to-b from-cyber-blue/15 via-cyber-blue/20 to-deepspace-400 rounded-3xl -z-10"></div>
              
              {/* Main Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyber-blue/20 via-cyber-blue/15 to-cyber-blue/10 rounded-full blur-[120px] scale-110 animate-pulse-slow"></div>

              {/* Main 3D Asset */}
              <div className="relative z-10 p-2 sm:p-4 bg-gradient-to-b from-cyber-blue/5 via-cyber-blue/3 to-transparent lg:bg-gradient-to-b lg:from-cyber-blue/3 lg:via-transparent lg:to-transparent rounded-3xl">
                <img 
                  src="/images/all/heroimage-blue.png" 
                  alt="AureumVault Security Platform" 
                  className="w-full h-auto object-contain animate-float drop-shadow-2xl"
                  style={{
                    filter: 'drop-shadow(0 0 100px rgba(0, 212, 255, 0.4)) drop-shadow(0 0 50px rgba(0, 212, 255, 0.3))'
                  }}
                />
              </div>

            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default HeroSection;